import {
    NarrativeItem,
    NarrativeStore,
    SupportingFact
} from '@assets/model/NarrativeStore';

import { useStore, useStoreUI } from '@assets/model/Store';
import { formatDivMarkup } from '@assets/utils/CompUtils';
import { observer } from 'mobx-react';
import { Fragment } from 'react';
import { ModalEditFact } from '../ModalEditFact/ModalEditFact';
import { ModalEditNarrative } from '../ModalEditNarrative/ModalEditNarrative';
import { Flex } from '../UI/Flex/Flex';
import * as icons from './icons/*.svg';
import * as style from './NarrativeElement.module.less';
import { getFirebase } from '@assets/firebase/FirebaseAdapater';
import { formatModuleVariableName } from '@assets/utils/CompUtils';
import { ModalDataLossPrompt } from '../modals/ModalDataLossPrompt/ModalDataLossPrompt';

interface NarrativeElementProps {
    item: NarrativeItem;
    narStore: NarrativeStore;
}

const NarrativeElement = observer((props: NarrativeElementProps) => {
    const { item: selectedInfo, narStore } = props;

    const onOpenEditItem = (itemId: string, narrative: NarrativeItem) => {
        const ui = useStoreUI();
        ui.modal = {
            title: 'Edit Narrative',
            el: (
                <ModalEditNarrative
                    hypothesis={narrative.hypothesis}
                    ui_display={narrative.ui_display}
                    onEdit={(hypothesis: string, ui_display: string) => {
                        onEditItemInDb(itemId, hypothesis, ui_display);
                        ui.modal = null;
                    }}
                    onDelete={() => {
                        onDeleteItemInDb(narrative);

                        ui.sideChatSelectedContext = null;
                        ui.modal = null;
                    }}
                />
            )
        };
    };

    const onRegenerateSection = (itemId: string, narrative: NarrativeItem) => {
        const ui = useStoreUI();
        ui.modal = {
            el: (
                <ModalDataLossPrompt
                    onVerifyDelete={() => {
                        onActualRegenSection();
                    }}
                    labels={{
                        bigWarning: `Are you sure you want to regenerate this section?`,
                        deleteDataName: `"${narrative.ui_displayIdGenerated} ${narrative.ui_display}"`,
                        smallWarning:
                            'This will permanently delete the current content',
                        approveActionText: 'Regenerate'
                    }}
                />
            ),
            size: 'M',
            title: 'Regenerate Section?'
        };
        const onActualRegenSection = () => {
            const fb = getFirebase();
            const store = useStore();
            fb.flowtask_regenerate_section(
                store.selectedReportId,
                narrative.itemId
            );
        };
    };

    const onRegenerateL4 = (itemId: string, narrative: NarrativeItem) => {
        const ui = useStoreUI();
        ui.modal = {
            title: 'Edit Narrative',
            size: 'M',
            el: (
                <ModalDataLossPrompt
                    onVerifyDelete={() => {
                        onActualRegenL4();
                    }}
                    labels={{
                        bigWarning: `Clear and re-generate supprting facts?`,
                        deleteDataName: `"${narrative.ui_displayIdGenerated} ${narrative.ui_display}"`,
                        smallWarning:
                            'This will permanently delete the current content',
                        approveActionText: 'Attach Facts'
                    }}
                />
            )
        };
        const onActualRegenL4 = () => {
            const fb = getFirebase();
            const store = useStore();
            fb.flowtask_add_attach_facts_for_section(
                store.selectedReportId,
                narrative.itemId
            );
        };
    };

    const onDeleteItemInDb = (narrative: NarrativeItem) => {
        narStore.removeItemFromParent(narrative);
    };

    const onEditItemInDb = (
        itemId: string,
        hypotehsis: string,
        ui_display
    ): void => {
        narStore.editNarrativeItem(itemId, hypotehsis, ui_display);
    };

    const onDeleteFact = (
        narItem: NarrativeItem,
        fact: SupportingFact,
        factIndex: number
    ) => {
        const ui = useStoreUI();
        const fact_str = fact.exact_fact_quote.slice(0, 25) + '...';
        ui.modal = {
            el: (
                <ModalDataLossPrompt
                    onVerifyDelete={() => {
                        narStore.hideSupportingFact(narItem.itemId, factIndex);
                    }}
                    labels={{
                        bigWarning: `Are you sure you want to delete this fact?`,
                        deleteDataName: `"${narItem.ui_displayIdGenerated}${factIndex + 1} - ${fact_str}"`,
                        smallWarning: 'This will permanently delete the fact',
                        approveActionText: 'Delete'
                    }}
                />
            ),
            size: 'M',
            title: 'Delete Fact?'
        };
    };

    const onOpenEditFact = (
        itemId: string,
        factIndex: number,
        fact: SupportingFact,
        factLabel: string
    ) => {
        const ui = useStoreUI();
        ui.modal = {
            title: `Edit ${factLabel}`,
            onHeaderDelete: () => {
                if (confirm('Are you sure?')) {
                    narStore.hideSupportingFact(itemId, factIndex);
                    ui.modal = null;
                }
            },
            el: (
                <ModalEditFact
                    fact={fact}
                    onEdit={(editedFact: SupportingFact) => {
                        narStore.editSupportingFact(
                            itemId,
                            factIndex,
                            editedFact
                        );
                        ui.modal = null;
                    }}
                />
            )
        };
    };

    const render_item_facts = (
        indentLevel: number,
        renderItem: NarrativeItem
    ): JSX.Element => {
        let facts =
            narStore.factsTable[renderItem.itemId]?.supporting_facts ?? [];

        //facts = facts.filter(f => !f.hidden);

        const dispId = renderItem.ui_displayIdGenerated;
        if (facts.length == 0) {
            return null;
        }

        const formattedFacts = facts.map((f, i) => {
            if (f.hidden) {
                return null;
            }
            const rawSource = f.fact_citation_source_file ?? '';
            return (
                <Flex
                    key={dispId + '_fact_' + i.toString()}
                    data-indent={indentLevel}
                    className={style.entryContainer}
                    style={{ marginLeft: indentLevel * 20 }}
                >
                    <div className={style.itemIdTitle}>
                        {dispId}
                        {i + 1}
                    </div>
                    <div className={style.blackLineSeperator} />
                    <div
                        style={{
                            flexGrow: 1,
                            overflow: 'auto',
                            marginRight: '10px'
                        }}
                    >
                        {formatDivMarkup(f.exact_fact_quote)}
                        <div className={style.source_module}>
                            Source: {formatModuleVariableName(rawSource)}
                        </div>
                    </div>
                    <div className={style.actionIcons}>
                        <img
                            src={icons['edit_item']}
                            title="Edit"
                            className={style.svgActionIcon}
                            onClick={() => {
                                onOpenEditFact(
                                    renderItem.itemId,
                                    i,
                                    f,
                                    `${dispId}${i + 1}`
                                );
                            }}
                        />
                    </div>
                    <div className={style.actionIcons}>
                        <img
                            src={icons['trash']}
                            className={style.svgActionIcon}
                            onClick={() => {
                                onDeleteFact(renderItem, f, i);
                            }}
                        />
                    </div>
                </Flex>
            );
        });

        return <>{formattedFacts.filter(e => e != null)}</>;
    };
    const render_item_with_indent = (
        indentLevel: number,
        renderItem: NarrativeItem
    ): JSX.Element => {
        const dispId = renderItem.ui_displayIdGenerated;
        const itemLevel = renderItem.level;

        return (
            <Fragment key={renderItem.itemId + '_fragment'}>
                <Flex
                    key={renderItem.itemId}
                    data-indent={indentLevel}
                    className={style.entryContainer}
                    style={{ marginLeft: indentLevel * 20 }}
                    id={'narrative_anchor_' + renderItem.itemId}
                >
                    <div className={style.itemIdTitle}>{dispId}</div>
                    <div className={style.blackLineSeperator} />
                    <div className={style.itemIdText} style={{ flexGrow: 1 }}>
                        {renderItem.hypothesis}
                    </div>
                    <div className={style.actionIcons}>
                        <img
                            src={icons['open_chat']}
                            className={style.svgActionIcon}
                            onClick={() => {
                                const ui = useStoreUI();
                                ui.sideChatSelectedContext = renderItem.itemId;
                            }}
                        />
                        {
                            <img
                                src={icons['edit_item']}
                                className={style.svgActionIcon}
                                onClick={() => {
                                    onOpenEditItem(
                                        renderItem.itemId,
                                        renderItem
                                    );
                                }}
                            />
                        }
                        <img
                            src={icons['regenerate_item']}
                            className={style.svgActionIcon}
                            onClick={() => {
                                onRegenerateSection(
                                    renderItem.itemId,
                                    renderItem
                                );
                            }}
                        />

                        {/* <img
                            src={icons['regenerate_item']}
                            className={style.svgActionIcon}
                            onClick={() => {
                                onRegenerateL4(renderItem.itemId, renderItem);
                            }}
                        /> */}
                    </div>
                </Flex>
                {renderItem.sub_items.map(e =>
                    render_item_with_indent(indentLevel + 1, e)
                )}
                {itemLevel == 'L3' &&
                    render_item_facts(indentLevel + 1, renderItem)}
            </Fragment>
        );
    };

    return (
        <div
            style={{ paddingBottom: '10px' }}
            id={'narrative_anchor_' + selectedInfo.itemId.replaceAll(':', '_')}
        >
            <div
                style={{
                    padding: '16px',
                    paddingTop: '0px'
                }}
            >
                <div style={{ overflowX: 'auto' }}>
                    <Flex direction="row" wrap="wrap">
                        {render_item_with_indent(0, selectedInfo)}
                    </Flex>
                </div>
            </div>
        </div>
    );
});

export default NarrativeElement;
